import { collection, doc, writeBatch } from '@firebase/firestore'
import { db } from 'config/firebase'
import { format, toZonedTime } from 'date-fns-tz'
import { loadThumbnailsinFirebase, ThumbnailRequest } from './scraperUtils'
import { SalePaymentType, Vehicle } from '@otw/models'

const SALE_PAYMENT_TYPE_LABELS: Record<SalePaymentType, string> = {
  cash: 'Cash',
  finance: 'Finance',
  customer_bank: "Customer's Bank",
}

export function formatSalePaymentType(type: SalePaymentType | string | null | undefined): string {
  if (!type) return ''
  const normalized = type.toLowerCase() as SalePaymentType
  return SALE_PAYMENT_TYPE_LABELS[normalized] ?? type
}

export function isBankBackedPaymentType(type: SalePaymentType | string | null | undefined): boolean {
  if (!type) return false
  const normalized = type.toLowerCase()
  return normalized === 'finance' || normalized === 'customer_bank'
}
import { vehicleStatusColorMap } from 'constants/inventory'

export function generateStockNumber(existingStockNumbers: string[], firstName: string, lastName: string) {
  const prefix = firstName.charAt(0).toUpperCase().concat(lastName.charAt(0).toUpperCase())
  const relevantCodes = existingStockNumbers
    .filter(code => code.startsWith(prefix))
    .map(code => parseInt(code.substring(2, 10)))
  const nextNumber = Math.max(...relevantCodes) + 1
  const nextCode = `${prefix}${nextNumber.toString().padStart(5, '0')}`
  return nextCode
}

export const getTextWidth = (text: string, font: string = '14px Arial'): number => {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (context) {
    context.font = font
    const metrics = context.measureText(text)
    return metrics.width
  }
  return 0
}

/**
 * Gets the style properties for a vehicle status
 * @param status The vehicle status
 * @returns An object with style properties (hex, color, backgroundColor, etc.)
 */
export const getVehicleStatusStyles = (status: string | undefined | null) => {
  if (!status) return vehicleStatusColorMap.default;
  
  // Return the color config for the status, or default if not found
  return vehicleStatusColorMap[status] || vehicleStatusColorMap.default;
};

export const formatPrice = (value: string | undefined | null | number) => {
  try {
    if (value === null || value === undefined) return ''
    if (typeof value === 'string' && (value.trim() === '' || isNaN(Number(value)))) return ''

    const numValue = typeof value === 'string' ? Number(value.replace(/[$,]/g, '')) : Number(value)
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: Number.isInteger(numValue) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(numValue)
  } catch (error) {
    return 'N/A'
  }
}

export const formatDistance = (value: string | undefined | null | number) => {
  try {
    if (value === null || value === undefined) return ''
    if (typeof value === 'string' && (value.trim() === '' || isNaN(Number(value)))) return ''

    const numValue = typeof value === 'string' ? Number(value.replace(/[^0-9.-]/g, '')) : Number(value)

    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue)
  } catch (error) {
    return 'N/A'
  }
}

export const formatPhoneNumber = (phoneNumber: string | null) => {
  if (!phoneNumber) return ''

  // Remove all non-digit characters
  const cleaned = phoneNumber.replace(/\D/g, '')

  // Format as standard US number: (123) 456-7890
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
  }

  // If not 10 digits, return as is with basic formatting
  return phoneNumber
}

export const formatDateTime = (
  value: string | undefined | null | { seconds: number; nanoseconds: number } | Date,
) => {
  try {
    if (!value) return ''

    let utcDate: Date
    if (typeof value === 'object' && 'seconds' in value) {
      // Handle Firebase Timestamp
      utcDate = new Date(value.seconds * 1000 + value.nanoseconds / 1000000)
    } else if (typeof value === 'string') {
      // Handle string date
      utcDate = new Date(value)
    } else if (value instanceof Date) {
      // Handle Date object
      utcDate = value
    } else {
      return ''
    }

    if (isNaN(utcDate.getTime())) {
      return ''
    }

    const easternTime = toZonedTime(utcDate, 'America/New_York')
    return format(easternTime, 'MM/dd/yyyy')
  } catch (error) {
    console.error('Error formatting date:', error)
    return ''
  }
}

export default { generateStockNumber }

function sanitizePrice(price: string) {
  const result = parseFloat(price.replace(/[^0-9.-]+/g, ''))
  return isNaN(result) ? null : result
}

export const calculateProfit = (buyPrice: string, sellPrice: string): string => {
  const buyPriceNum = sanitizePrice(buyPrice)
  const sellPriceNum = sanitizePrice(sellPrice)

  if (!buyPriceNum || !sellPriceNum) {
    return ''
  }
  return String(sellPriceNum - buyPriceNum)
}

export const sanitizeNumber = (value: string | number | boolean) => {
  if (typeof value === 'string') {
    return value.replace(/,/g, '')
  }

  return value
}

export const sanitizeVehicleData = (
  data: { [key: string]: any },
  vehicle?: Vehicle | null | any,
): { [key: string]: any } | null => {
  // Create a copy of the data to avoid modifying the original
  const sanitizedData = { ...data }

  // Handle number fields if they are present
  const numberFields = [
    'miles',
    'optic_list_price',
    'seller_asking_price',
    'profit',
    'repair_cost',
    'estimated_body',
    'estimated_mechanical',
    'estimated_safety',
    'estimated_repair_cost',
    'shipping_cost',
    'rental_cost',
    'other_cost',
    'payoff_amount',
    'year',
  ]
  for (const field of numberFields) {
    if (sanitizedData[field] != undefined) {
      const value = sanitizeNumber(sanitizedData[field])
      if (typeof value === 'boolean') {
        sanitizedData[field] = Number(value)
      } else if (typeof value === 'string') {
        sanitizedData[field] = isNaN(Number(value)) ? null : Number(value)
      } else {
        sanitizedData[field] = isNaN(value) ? null : value
      }
    }
  }

  // Calculate profit if prices are being changed
  if (
    (sanitizedData.optic_list_price != undefined || sanitizedData.seller_asking_price != undefined) &&
    !sanitizedData.profit
  ) {
    const sellerPrice = sanitizedData.seller_asking_price ?? vehicle?.seller_asking_price ?? 0
    const opticPrice = sanitizedData.optic_list_price ?? vehicle?.optic_list_price ?? 0
    if (sellerPrice && opticPrice) {
      sanitizedData.profit = opticPrice - sellerPrice
    }
  }

  // Handle status changes to/from "sold"
  if (sanitizedData.status) {
    const newStatus = String(sanitizedData.status).toLowerCase().trim()

    // If changing to sold, ensure show_on_website is false and set sold_date if not provided
    if (newStatus === 'sold') {
      sanitizedData.show_on_website = false
      // Always set sold_date to current date when marking as sold if not explicitly provided
      if (!sanitizedData.sold_date && !vehicle?.sold_date) {
        // Create a date set to noon today to avoid timezone issues
        const today = new Date();
        today.setHours(12, 0, 0, 0); // Set to noon local time
        sanitizedData.sold_date = today.toISOString();
      }
    } else if (newStatus === 'sold elsewhere') {
      sanitizedData.show_on_website = false
    }
  }

  // Handle paid_for changes - set paid_for_date when paid_for is set to true
  if (sanitizedData.paid_for !== undefined) {
    if (sanitizedData.paid_for === true) {
      // Set paid_for_date to current date and time if not already provided
      if (!sanitizedData.paid_for_date && !vehicle?.paid_for_date) {
        sanitizedData.paid_for_date = new Date().toISOString()
      }
    } else if (sanitizedData.paid_for === false) {
      // Clear paid_for_date when paid_for is set to false
      sanitizedData.paid_for_date = null
    }
  }

  // Handle funded changes - set funded_date when funded is set to true
  if (sanitizedData.funded !== undefined) {
    if (sanitizedData.funded === true) {
      // Set funded_date to current date and time if not already provided
      if (!sanitizedData.funded_date && !vehicle?.funded_date) {
        sanitizedData.funded_date = new Date().toISOString()
      }
    } else if (sanitizedData.funded === false) {
      // Clear funded_date when funded is set to false
      sanitizedData.funded_date = null
    }
  }

  // Ensure sold_date is properly formatted if it's being changed
  if (sanitizedData.sold_date != null && sanitizedData.sold_date !== '') {
    try {
      // Try to parse the date to ensure it's valid
      const dateObj = new Date(sanitizedData.sold_date)
      if (!isNaN(dateObj.getTime())) {
        // Format as ISO string for consistency
        sanitizedData.sold_date = dateObj.toISOString()
      } else {
        console.error('Invalid sold date format')
        return null
      }
    } catch (error) {
      console.error('Invalid sold date format', error)
      return null
    }
  }

  // Ensure paid_for_date is properly formatted if it's being changed
  if (sanitizedData.paid_for_date != null && sanitizedData.paid_for_date !== '') {
    try {
      // Try to parse the date to ensure it's valid
      const dateObj = new Date(sanitizedData.paid_for_date)
      if (!isNaN(dateObj.getTime())) {
        // Format as ISO string for consistency
        sanitizedData.paid_for_date = dateObj.toISOString()
      } else {
        console.error('Invalid paid_for_date format')
        return null
      }
    } catch (error) {
      console.error('Invalid paid_for_date format', error)
      return null
    }
  }

  // Ensure funded_date is properly formatted if it's being changed
  if (sanitizedData.funded_date != null && sanitizedData.funded_date !== '') {
    try {
      // Try to parse the date to ensure it's valid
      const dateObj = new Date(sanitizedData.funded_date)
      if (!isNaN(dateObj.getTime())) {
        // Format as ISO string for consistency
        sanitizedData.funded_date = dateObj.toISOString()
      } else {
        console.error('Invalid funded_date format')
        return null
      }
    } catch (error) {
      console.error('Invalid funded_date format', error)
      return null
    }
  }

  // Ensure pending_sale_date is properly formatted if it's being changed
  if (sanitizedData.pending_sale_date != null && sanitizedData.pending_sale_date !== '') {
    try {
      const dateObj = new Date(sanitizedData.pending_sale_date)
      if (!isNaN(dateObj.getTime())) {
        sanitizedData.pending_sale_date = dateObj.toISOString()
      } else {
        console.error('Invalid pending_sale_date format')
        return null
      }
    } catch (error) {
      console.error('Invalid pending_sale_date format', error)
      return null
    }
  }

  // Ensure active_deal_date is properly formatted if it's being changed
  if (sanitizedData.active_deal_date != null && sanitizedData.active_deal_date !== '') {
    try {
      const dateObj = new Date(sanitizedData.active_deal_date)
      if (!isNaN(dateObj.getTime())) {
        sanitizedData.active_deal_date = dateObj.toISOString()
      } else {
        console.error('Invalid active_deal_date format')
        return null
      }
    } catch (error) {
      console.error('Invalid active_deal_date format', error)
      return null
    }
  }

  // Ensure pending_available_date is properly formatted if it's being changed
  if (sanitizedData.pending_available_date != null && sanitizedData.pending_available_date !== '') {
    try {
      const dateObj = new Date(sanitizedData.pending_available_date)
      if (!isNaN(dateObj.getTime())) {
        sanitizedData.pending_available_date = dateObj.toISOString()
      } else {
        console.error('Invalid pending_available_date format')
        return null
      }
    } catch (error) {
      console.error('Invalid pending_available_date format', error)
      return null
    }
  }

  // Ensure available_date is properly formatted if it's being changed
  if (sanitizedData.available_date != null && sanitizedData.available_date !== '') {
    try {
      const dateObj = new Date(sanitizedData.available_date)
      if (!isNaN(dateObj.getTime())) {
        sanitizedData.available_date = dateObj.toISOString()
      } else {
        console.error('Invalid available_date format')
        return null
      }
    } catch (error) {
      console.error('Invalid available_date format', error)
      return null
    }
  }

  // Filter out undefined values and return
  return Object.fromEntries(Object.entries(sanitizedData).filter(([, value]) => value !== undefined))
}

export function mapPartialToFullVehicle(partialVehicle: Partial<Vehicle>): Vehicle {
  const currentDate = new Date().toISOString()

  return {
    id: String(partialVehicle.id || ''),
    updated_by: null,
    updated_at: null,
    date_added: String(partialVehicle.date_added || currentDate),
    vin: String(partialVehicle.vin || ''),
    who: String(partialVehicle.who || ''),
    stock_number: String(partialVehicle.stock_number || ''),
    year: Number(partialVehicle.year || 0),
    make: String(partialVehicle.make || ''),
    model: String(partialVehicle.model || ''),
    size: String(partialVehicle.size || ''),
    miles: Number(partialVehicle.miles || 0),
    fuel: partialVehicle.fuel as 'Gas' | 'Diesel' | null,
    location: String(partialVehicle.location || ''),
    type: String(partialVehicle.type || ''),
    optic_list_price: Number(partialVehicle.optic_list_price || 0),
    seller_asking_price: Number(partialVehicle.seller_asking_price || 0),
    profit: Number(partialVehicle.profit || 0),
    sold_date: String(partialVehicle.sold_date || currentDate),
    truck_notes: String(partialVehicle.truck_notes || ''),
    phone_number: String(partialVehicle.phone_number || ''),
    email: String(partialVehicle.email || ''),
    follow_up_notes: String(partialVehicle.follow_up_notes || ''),
    entered_by: String(partialVehicle.entered_by || ''),
    payoff_amount: Number(partialVehicle.payoff_amount || 0),
    link: String(partialVehicle.link || ''),
    show_on_website: partialVehicle.show_on_website || false,
    photo_storage_path: String(partialVehicle.photo_storage_path || ''),
    photos: partialVehicle.photos || {
      main: '',
      front: '',
      back: '',
      left: '',
      right: '',
      interior: '',
      engine: '',
      trunk: '',
      other: '',
    },
    status: partialVehicle.status || 'Available',
    condition: partialVehicle.condition || 'New',
    sold_to_user: null,
    dealQuality: partialVehicle.dealQuality || 'good',
    sold_by_user: null,
    selling_to_user: null,
    cameras: partialVehicle.cameras || 'unknown',
    shelves: partialVehicle.shelves || 'unknown',
    size_confirmed: false,
    date_available: null,
    gwvr_over_10k: 'unknown' as const,
    bought_from: null,
    purchased_for_inventory: false,
    documents: {
      title_copies: [],
      purchase_orders: [],
    },
    ready_for_transport: partialVehicle.ready_for_transport || null,
  }
}

export async function storeImportedVehicles(
  vehicles: Vehicle[],
  collectionName: string,
  onProgress: (percent: number) => void,
  subcollection?: {
    name: string
    parentDocId: string
  },
) {
  const processedVehicles = []
  const BATCH_SIZE = 500

  // Create collection ref based on whether subcollection is specified
  const collectionRef = subcollection
    ? collection(db, collectionName, subcollection.parentDocId, subcollection.name)
    : collection(db, collectionName)

  for (let i = 0; i < vehicles.length; i += BATCH_SIZE) {
    const batch = writeBatch(db)
    const vehicleBatch = vehicles.slice(i, i + BATCH_SIZE)
    const downloadBatch: ThumbnailRequest[] = []

    for (let j = 0; j < vehicleBatch.length; j++) {
      const vehicle = vehicleBatch[j]
      try {
        const vehicleRef = doc(collectionRef)
        const newVehicleId = vehicleRef.id

        const storagePath = subcollection
          ? `${collectionName}/${subcollection.parentDocId}/${subcollection.name}/${newVehicleId}/thumbnail.jpeg`
          : `${collectionName}/${newVehicleId}/thumbnail.jpeg`

        const vehicleData = {
          ...vehicle,
          id: newVehicleId,
        }

        batch.set(vehicleRef, vehicleData)
        downloadBatch.push({
          firebasePath: storagePath,
          url: vehicle.link || '',
        })
        processedVehicles.push(vehicleData)

        const progress = ((i + j + 1) / vehicles.length) * 100
        onProgress(progress)
      } catch (error) {
        console.error(`Error processing vehicle ${vehicle.id}:`, error)
      }
    }

    try {
      await loadThumbnailsinFirebase({
        scrapeThumbnailRequests: downloadBatch,
      })
      await batch.commit()
    } catch (error) {
      console.error('Error committing batch:', error)
      throw error
    }
  }

  return processedVehicles
}
